import { useLogto } from '@logto/react';
import { type SignInExperience } from '@logto/schemas';
import { useEffect, useRef } from 'react';
import useSWR from 'swr';

import { type RequestError } from './use-api';
import useRedirectUri from './use-redirect-uri';

const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

/**
 * Signs the admin out of the console after `mfa.idleTimeoutMinutes` minutes of inactivity.
 * Client-side only — the OIDC Session TTL is unaffected. Absent/zero config disables the timer.
 */
const useIdleLogout = () => {
  const { isAuthenticated, signOut } = useLogto();
  const postSignOutRedirectUri = useRedirectUri('signOut');
  // Shares the SWR cache key used elsewhere in the console — no extra request.
  const { data } = useSWR<SignInExperience, RequestError>(isAuthenticated && 'api/sign-in-exp');
  const idleTimeoutMinutes = data?.mfa.idleTimeoutMinutes;
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!isAuthenticated || !idleTimeoutMinutes) {
      return;
    }

    const reset = () => {
      clearTimeout(timer.current);
      // eslint-disable-next-line @silverhand/fp/no-mutation
      timer.current = setTimeout(() => {
        void signOut(postSignOutRedirectUri.href);
      }, idleTimeoutMinutes * 60_000);
    };

    for (const event of activityEvents) {
      window.addEventListener(event, reset, { passive: true });
    }
    reset();

    return () => {
      clearTimeout(timer.current);
      for (const event of activityEvents) {
        window.removeEventListener(event, reset);
      }
    };
  }, [isAuthenticated, idleTimeoutMinutes, signOut, postSignOutRedirectUri.href]);
};

export default useIdleLogout;
